import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { simulation, tripNow } from '../clock.ts';
import { cx } from '../kit/cx.ts';
import { DataTag } from '../kit/index.ts';
import m from '../kit/Motifs.module.css';
import t from '../kit/type.module.css';
import s from './SimulationBand.module.css';

/** Re-renders with the trip clock: each second while it runs, and whenever it's moved. */
export function useTripClock(): number {
  const [now, setNow] = useState(tripNow);
  useEffect(() => {
    const update = () => setNow(tripNow());
    window.addEventListener('pf-clock', update);
    const timer = simulation() ? setInterval(update, 1000) : undefined;
    return () => {
      window.removeEventListener('pf-clock', update);
      clearInterval(timer);
    };
  }, []);
  return now;
}

/** The apps' simulation band on the web (IMPL-CONNECT A1, S7): simulated, the trip time, the speed. */
export function SimulationBand() {
  const now = useTripClock();
  const sim = simulation();
  if (!sim) return null;
  const zone = sim.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'shortOffset',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return (
    <div className={s.band} role="status">
      <DataTag label="Simulated" tone="signal" />
      <span className={cx(t.readoutS, s.time)}>
        {part('month')}.{part('day')} {part('hour')}:{part('minute')} {part('timeZoneName')}
      </span>
      <span className={cx(t.readoutS, s.speed)}>{sim.paused ? 'paused' : `×${sim.speed}`}</span>
      <Link to="/dev" className={cx(t.label, s.link)}>
        Simulator
      </Link>
      <span className={cx(s.stripes, m.hazard)} aria-hidden="true" />
    </div>
  );
}
